import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import { WHATSAPP_NUMBER } from './utils/constants';

import Home from './pages/Home';
import About from './pages/About';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import Contact from './pages/Contact';
import Partnership from './pages/Partnership';
import CityPage from './pages/CityPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Disclaimer from './pages/Disclaimer';

import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProperties from './pages/admin/AdminProperties';
import AdminPropertyForm from './pages/admin/AdminPropertyForm';
import AdminLeads from './pages/admin/AdminLeads';
import AdminChangePassword from './pages/admin/AdminChangePassword';
import AdminResetPassword from './pages/admin/AdminResetPassword';

const WaButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello,%20I'm%20interested%20in%20a%20property.%20Please%20share%20details.`}
    className="sticky-whatsapp" target="_blank" rel="noreferrer" aria-label="WhatsApp"
  >
    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" width="28"/>
  </a>
);

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <WaButton />
  </>
);

const PropertyLayout = ({ children }) => (
  <>
    {children}
    <WaButton />
  </>
);

const PrivateRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}>Loading...</div>;
  return admin ? children : <Navigate to="/admin/login" replace />;
};

const App = () => {
  useEffect(() => {
    // Disable right-click context menu on public site only
    const blockContext = (e) => {
      if (!window.location.pathname.startsWith('/admin')) e.preventDefault();
    };
    document.addEventListener('contextmenu', blockContext);
    return () => document.removeEventListener('contextmenu', blockContext);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" toastOptions={{ style: { fontFamily: 'var(--font-body)', fontSize: '14px' } }} />
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/properties" element={<PublicLayout><Properties /></PublicLayout>} />
          <Route path="/properties/:slug" element={<PropertyLayout><PropertyDetail /></PropertyLayout>} />
          <Route path="/projects" element={<Navigate to="/properties" replace />} />
          <Route path="/projects/:slug" element={<Navigate to="/properties" replace />} />
          <Route path="/cities/:city" element={<PublicLayout><CityPage /></PublicLayout>} />
          <Route path="/partnership" element={<PublicLayout><Partnership /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
          <Route path="/disclaimer" element={<PublicLayout><Disclaimer /></PublicLayout>} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
          <Route path="/admin/properties" element={<PrivateRoute><AdminProperties /></PrivateRoute>} />
          <Route path="/admin/properties/new" element={<PrivateRoute><AdminPropertyForm /></PrivateRoute>} />
          <Route path="/admin/properties/:id/edit" element={<PrivateRoute><AdminPropertyForm /></PrivateRoute>} />
          <Route path="/admin/leads" element={<PrivateRoute><AdminLeads /></PrivateRoute>} />
          <Route path="/admin/change-password" element={<PrivateRoute><AdminChangePassword /></PrivateRoute>} />

          <Route path="*" element={<PublicLayout><div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'16px'}}><h1 style={{fontFamily:'var(--font-display)',fontSize:'60px',color:'var(--navy)'}}>404</h1><p>Page not found</p><a href="/" className="btn-primary">Go Home</a></div></PublicLayout>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
